import { Component, input, output } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { TPipe } from '../../../../shared/pipes';
import type { NotificationItem } from '../../models/notifications.models';
import { NotificationItemComponent } from '../notification-item/notification-item.component';

@Component({
  selector: 'app-notifications-dropdown',
  standalone: true,
  imports: [TPipe, LucideAngularModule, NotificationItemComponent],
  template: `
    <section class="dropdown">
      <header class="dropdown-header">
        <h4>{{ 'notifications.title' | t }}</h4>
      </header>

      @if (loading()) {
        <div class="state">
          <lucide-icon name="loader-circle" class="h-4 w-4 spin"></lucide-icon>
          <p>{{ 'notifications.loading' | t }}</p>
        </div>
      } @else if (error()) {
        <div class="state">
          <p>{{ 'notifications.error' | t }}</p>
          <button type="button" class="ghost-btn" (click)="retry.emit()">{{ 'common.retry' | t }}</button>
        </div>
      } @else if (isEmpty()) {
        <div class="state">
          <p>{{ 'notifications.empty' | t }}</p>
        </div>
      } @else {
        <div class="list-scroll" (scroll)="onScroll($event)">
          <div class="list">
            @for (item of items(); track item.id) {
              <app-notification-item [item]="item" (open)="openItem.emit($event)"></app-notification-item>
            }
          </div>
          @if (loadingMore()) {
            <div class="loading-more">
              <lucide-icon name="loader-circle" class="h-4 w-4 spin"></lucide-icon>
            </div>
          }
        </div>
      }

      <footer class="dropdown-footer">
        <button
          type="button"
          class="ghost-btn ghost-btn--block"
          [disabled]="!hasUnread()"
          (click)="markAllRead.emit()"
        >
          {{ 'notifications.markAllRead' | t }}
        </button>
      </footer>
    </section>
  `,
  styles: [
    `
      .dropdown {
        width: min(380px, calc(100vw - 24px));
        max-height: min(560px, calc(100vh - 100px));
        border: 1px solid var(--border);
        border-radius: 12px;
        background: var(--bg-surface);
        box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
        padding: 10px;
        display: grid;
        grid-template-rows: auto 1fr auto;
        gap: 8px;
        min-height: 120px;
      }

      .dropdown-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
      }

      h4 {
        margin: 0;
        font-size: 14px;
        color: var(--text);
      }

      .list-scroll {
        min-height: 0;
        max-height: min(400px, 52vh);
        overflow-y: auto;
        overflow-x: hidden;
        border-radius: 10px;
        padding-right: 2px;
      }

      .list {
        display: grid;
        gap: 8px;
      }

      .loading-more {
        display: flex;
        justify-content: center;
        padding: 10px 0 4px;
        color: var(--muted);
      }

      .dropdown-footer {
        flex-shrink: 0;
        padding-top: 4px;
        border-top: 1px solid var(--border);
      }

      .ghost-btn {
        height: 32px;
        border-radius: 8px;
        border: 1px solid var(--border);
        background: var(--surface-elevated);
        color: var(--text);
        padding: 0 12px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
      }

      .ghost-btn:hover:not(:disabled) {
        background: var(--hover);
      }

      .ghost-btn:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }

      .ghost-btn--block {
        width: 100%;
      }

      .state {
        min-height: 90px;
        border: 1px dashed color-mix(in srgb, var(--border) 80%, transparent);
        border-radius: 10px;
        display: grid;
        place-content: center;
        justify-items: center;
        gap: 8px;
        text-align: center;
      }

      .state p {
        margin: 0;
        color: var(--muted);
        font-size: 12px;
      }

      .spin {
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ],
})
export class NotificationsDropdownComponent {
  readonly items = input<NotificationItem[]>([]);
  readonly loading = input(false);
  readonly loadingMore = input(false);
  readonly error = input<string | null>(null);
  readonly hasUnread = input(false);
  readonly isEmpty = input(false);
  readonly hasMore = input(false);

  readonly retry = output<void>();
  readonly markAllRead = output<void>();
  readonly loadMore = output<void>();
  readonly openItem = output<NotificationItem>();

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const threshold = 72;
    const distanceBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceBottom > threshold) return;
    if (!this.hasMore() || this.loadingMore() || this.loading()) return;
    this.loadMore.emit();
  }
}
